import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

/**
 * Slot map handed to `DayPicker`'s `classNames`, keyed by its `UI` enum.
 *
 * The range highlight is drawn on the cell (`day`) while the selected pill is
 * drawn on the button inside it — that split is what lets the continuous band
 * run edge to edge between two round endpoints.
 */
export const calendarVariants = tv({
  slots: {
    // `relative` anchors the absolutely-positioned nav to the calendar; without it
    // the chevrons position against whatever ancestor happens to be positioned.
    root: 'relative w-fit select-none',
    months: 'flex gap-0',
    month: 'flex flex-col gap-2 px-4 py-3',
    nav: 'absolute inset-x-0 top-3 flex items-center justify-between px-4',
    navButton: [
      'flex size-6 items-center justify-center rounded-sm text-text-action-active transition-colors',
      'hover:bg-states-focus hover:text-text-primary',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-main',
      'disabled:pointer-events-none disabled:text-text-disabled',
    ],
    monthCaption: 'flex h-6 items-center justify-center',
    captionLabel: [typography.subtitle1, 'text-text-primary'],
    monthGrid: 'border-collapse',
    weekdays: 'flex',
    weekday: [
      typography.caption,
      'flex size-9 items-center justify-center font-medium text-text-secondary',
    ],
    weeks: '',
    week: 'flex',
    day: [
      'relative p-0 text-center',
      // A day from an adjacent month is a duplicate of a real cell in the other
      // displayed month, so it must never take selection styling: a range ending
      // on 2 December was painting a green pill both there and in November's
      // trailing week. Attribute selectors outrank the selection slot classes,
      // which react-day-picker concatenates in its own order.
      'data-[outside]:!bg-transparent',
      '[&[data-outside]_button]:!bg-transparent [&[data-outside]_button]:!text-text-disabled',
      '[&[data-outside]_button]:!ring-0',
    ],
    dayButton: [
      typography.body2,
      'relative flex size-9 items-center justify-center rounded-full bg-transparent text-text-primary',
      'transition-colors focus-visible:outline-none',
      'hover:bg-paper-elevation6',
      'focus-visible:ring-1 focus-visible:ring-primary-main',
      'active:bg-paper-elevation7',
    ],
    today: '[&_button]:ring-1 [&_button]:ring-inset [&_button]:ring-text-secondary',
    outside: '[&_button]:text-text-disabled',
    disabled: '[&_button]:pointer-events-none [&_button]:text-text-disabled',
    hidden: 'invisible',
    // Square band behind the middle of a range; the endpoints round it off.
    // react-day-picker marks range middles `selected` too, so the selected pill's
    // dark-on-light text has to be undone here or the middle reads as black on dark.
    rangeMiddle:
      'bg-states-focus [&_button]:bg-transparent [&_button]:text-text-primary [&_button]:hover:bg-paper-elevation6',
    // The `hover:` variants are repeated on purpose: the button's own
    // `hover:bg-paper-elevation6` has the same specificity as these parent-driven
    // rules, so without them hovering a selected day repaints it as unselected.
    rangeStart: [
      'rounded-l-full bg-states-focus',
      '[&_button]:bg-primary-main [&_button]:text-primary-contrastText',
      '[&_button]:hover:bg-primary-main [&_button]:active:bg-primary-main',
    ],
    rangeEnd: [
      'rounded-r-full bg-states-focus',
      '[&_button]:bg-primary-main [&_button]:text-primary-contrastText',
      '[&_button]:hover:bg-primary-main [&_button]:active:bg-primary-main',
    ],
    selected: [
      '[&_button]:bg-primary-main [&_button]:text-primary-contrastText',
      '[&_button]:hover:bg-primary-main [&_button]:active:bg-primary-main',
    ],
  },
  variants: {
    size: {
      small: {
        day: 'size-9',
      },
      // The touch sheet (Figma PLO-Mobile-Study 694:355361): 32px square month
      // buttons with 20px chevrons on a 40px caption row, `body2` weekdays, and
      // 36px days 2px apart. Each cell is its 36px button plus 1px padding a side,
      // so the range band on the cell stays continuous while the pills keep their
      // 2px spacing.
      medium: {
        // Figma's month row spans the 320px calendar, not the 266px grid, so the
        // arrows sit outside the first and last day columns.
        month: 'w-80 gap-0 p-0',
        monthGrid: 'mx-auto',
        nav: 'top-0 px-6',
        navButton: 'size-8 bg-button-secondary-main text-text-primary [&_svg]:size-5',
        monthCaption: 'h-10',
        weekday: [typography.body2, 'mx-0.25'],
        weeks: 'flex flex-col gap-0.5 pt-3',
        day: 'box-content h-9 min-w-9 px-0.25',
        // Rows are 2px apart, so each week's part of the band is its own pill.
        rangeMiddle: 'first:rounded-l-full last:rounded-r-full',
        rangeStart: 'last:rounded-r-full',
        rangeEnd: 'first:rounded-l-full',
      },
    },
  },
  defaultVariants: {
    size: 'small',
  },
})
